import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { SignUpCommandOutput } from '@aws-sdk/client-cognito-identity-provider'
import { useAppContext } from 'context/AppContext'
import { AuthContext, useAuth, default_primary_color } from 'context/AuthContext'
import { classNames } from 'utils/dom-helpers'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import { SubmitError } from '../types'
import { CognitoErrorTypes } from 'types/cognito'
import { useRouter } from 'next/router'
import Translation from 'components/Translation'
import { createOrUpdate } from 'services/client'
import { ClientApiPostRequest } from 'types/client'
import { useTenantContext } from 'context/TenantContext'
import { EMAIL_REGEX, WORD_SINGLE_LINE_REGEX, PHONE_REGEX } from 'types/common'
import InputValidations from 'components/Validations/Field'

type FormValues = {
    first_name: string
    last_name: string
    email: string
    phone: string
    password: string
    confirm_password: string
}

const ModalProvider = createModal(
    AuthContext,
    'ClientSignupModal',
    () => <span>Sign Up</span>,
    undefined,
    undefined
)

export const ClientSignupModalOpener = ModalProvider.Opener
export const ClientSignupModalCloser = ModalProvider.Closer

function ClientSignupModal() {
    const router = useRouter()
    const ctx = useAuth()
    const { tenant, primaryColor } = useTenantContext()
    const { translations, loadComponentTranslations } = useAppContext()

    const [agree, toggleAgree] = useState<boolean>(false)
    const [loading, toggleLoading] = useState(false)
    const [type_password, setTypePassword] = useState(true)
    const [confirm_password, setConfirmPassword] = useState(true)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
        setValue
    } = useForm<FormValues>({
        mode: 'onSubmit',
    })

    const onSubmit: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            const {
                email,
                password,
                confirm_password,
                first_name,
                last_name,
                phone,
            } = values
            if (password != confirm_password) {
                throw {
                    name: CognitoErrorTypes.PasswordMismatch,
                } as SubmitError
            }

            const cognito_res: SignUpCommandOutput = (await ctx.signup(
                email,
                password,
                first_name,
                last_name,
                'CLIENT',
                tenant?.id
            )) as SignUpCommandOutput

            if (cognito_res && tenant?.id) {
                const user = {
                    email,
                    firstName: first_name,
                    lastName: last_name,
                    phone: phone,
                    timezone: 'Asia/Singapore',
                }

                const clientDto: ClientApiPostRequest = {
                    cognitoId: cognito_res.UserSub as string,
                    tenant,
                    user,
                }

                const client = await createOrUpdate(clientDto)

                if (client) {
                    reset()
                    ctx.EmailVerificationModal.setAttributes({ 
                        email,
                        redirect: 'client_login' 
                    })
                    ctx.EmailVerificationModal.open()
                }
            }
        } catch (e) {
            const { name: error_name, message } = e as SubmitError
            if (error_name == CognitoErrorTypes.UserExistsException) {
                setError('email', {
                    type: CognitoErrorTypes.UserExistsException,
                    message,
                })
            }
            if (error_name == CognitoErrorTypes.InvalidPasswordException) {
                setError('password', {
                    type: CognitoErrorTypes.InvalidPasswordException,
                    message,
                })
            }
            if (error_name == CognitoErrorTypes.PasswordMismatch) {
                setError('password', {
                    type: CognitoErrorTypes.PasswordMismatch,
                    message: 'Password Mismatch',
                })
            }
        }
        toggleLoading(false)
    }

    const getModalCloser = () => (
        <div
            onClick={() => {
                reset()
                ctx.ClientSignupModal.close()
            }}
            className="absolute top-8 right-8"
        >
            <ClientSignupModalCloser className="self-center" />
        </div>
    )

    useEffect(() => {
        if (!translations.SIGNUP_MODAL_TRANSLATION_ID) {
            loadComponentTranslations('SIGNUP_MODAL_TRANSLATION_ID')
        }
    }, [translations])

    useEffect(() => {
        if (ctx.ClientSignupModal?.attributes?.pre_populate) {
            setValue('first_name', localStorage.getItem('first_name') as string)
            setValue('last_name', localStorage.getItem('last_name') as string)
            setValue('email', localStorage.getItem('email') as string)
            setValue('phone', localStorage.getItem('phone') as string)
        }
    }, [ctx.ClientSignupModal?.attributes?.pre_populate])

    return (
        <ModalProvider.Visible>
            <ModalWrapper className="w-wide p-3">
                <div className="shadow-3xl rounded-lg mx-auto relative flex flex-col py-8 bg-white bg-cover bg-center w-full items-center
                    w-full sm:w-full md:w-full lg:w-2/3 xl:w-2/3
                    h-auto sm:h-4/5 lg:h-auto 2xl:h-auto 
                    m-auto relative flex p-0">
                    <div className="w-full py-8 px-10">
                        {getModalCloser()}
                    </div>
                    <div className="md:mt-5 px-10 flex text-center text-lg sm:text-3xl md:text-4xl lg:text-2xl 2xl:text-3xl">
                        <Translation
                            content_key="booking.sign_up_to"
                            content_values={{
                                tenant_name: tenant?.name as string,
                            }}
                        />
                    </div>

                    <form
                        method="POST"
                        onSubmit={handleSubmit(onSubmit)}
                        className="z-20 pt-12 px-10 w-full flex justify-center "
                    >
                        <div className="w-full">
                            <div className="pb-6 lg:flex">
                                <fieldset className="pb-6 lg:pb-0 lg:w-1/2 lg:pr-2">
                                    <label
                                        htmlFor="first_name"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.your_first_name" />
                                        <span className="text-red-500">
                                            {' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        placeholder={
                                            translations['booking.enter_your_first_name']
                                        }
                                        type="text"
                                        id="first_name"
                                        autoComplete="first_name"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.first_name?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('first_name', {
                                            required: true,
                                            maxLength: 50,
                                            pattern: WORD_SINGLE_LINE_REGEX 
                                        })}
                                    />
                                    <InputValidations
                                        field_error={errors.first_name}
                                        required_key="booking_page.profile_first_name_required"
                                        pattern_key='invalid.pattern.first_name'
                                    />
                                </fieldset>
                                <fieldset className="lg:w-1/2 lg:pl-2">
                                    <label
                                        htmlFor="last_name"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.your_last_name" />
                                        <span className="text-red-500">
                                            {' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        placeholder={
                                            translations['booking.enter_your_last_name']
                                        }
                                        type="text"
                                        id="last_name"
                                        autoComplete="last_name"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.last_name?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('last_name', {
                                            required: true,
                                            maxLength: 50,
                                            pattern: WORD_SINGLE_LINE_REGEX 
                                        })}
                                    />
                                    <InputValidations
                                        field_error={errors.last_name}
                                        required_key="booking_page.profile_last_name_required"
                                        pattern_key='invalid.pattern.last_name'
                                    />
                                </fieldset>
                            </div>

                            <div className="pb-6 lg:flex">
                                <fieldset className="pb-6 lg:pb-0 lg:w-1/2 lg:pr-2">
                                    <label
                                        htmlFor="email_address"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.email_address_label" />
                                        <span className="text-red-500">
                                            {' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        placeholder={
                                            translations[
                                            'booking.enter_email'
                                            ]
                                        }
                                        type="text"
                                        id="email_address"
                                        autoComplete="email"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.email?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('email', {
                                            required: true,
                                            pattern: EMAIL_REGEX
                                        })}
                                    />
                                    <InputValidations
                                        field_error={errors.email}
                                        required_key="booking.email_required"
                                        pattern_key='booking.valid_email_required'
                                    />
                                </fieldset>
                                <fieldset className="lg:w-1/2 lg:pl-2">
                                    <label
                                        htmlFor="phone"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.phone" />
                                    </label>
                                    <input
                                        placeholder={
                                            translations['booking.enter_phone']
                                        }
                                        type="text"
                                        id="phone"
                                        autoComplete="phone"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.phone?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('phone', {
                                            pattern: PHONE_REGEX
                                        })}
                                    />
                                    <InputValidations
                                        field_error={errors.phone}
                                        pattern_key='common.valid_phone_required'
                                    />
                                </fieldset>
                            </div>

                            <div className="pb-6 lg:flex">
                                <fieldset className="pb-6 lg:pb-0 lg:w-1/2 lg:pr-2 relative">
                                    <label
                                        htmlFor="password"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.password_label" />
                                        <span className="text-red-500">
                                            {' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        placeholder={
                                            translations['booking.enter_password']
                                        }
                                        type={type_password ? 'password' : 'text'}
                                        id="password"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.password?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('password', {
                                            required: true,
                                            minLength: 8,
                                        })}
                                    />

                                    <i className={type_password ? "flex justify-end right-0 -mt-8 mr-4 absolute feather-eye " : "feather-eye-off absolute right-0 flex justify-end -mt-8 mr-4"} onClick={() => setTypePassword(!type_password)} />
                                    <InputValidations
                                        field_error={errors.password}
                                        className="mb-1"
                                        required_key="booking.password_required"
                                        minlength_key="booking.valid_password_required"
                                    />
                                </fieldset>
                                <fieldset className="lg:w-1/2 lg:pl-2 relative">
                                    <label
                                        htmlFor="confirm_password"
                                        className={
                                            'block font-bold text-gray-600'
                                        }
                                    >
                                        <Translation content_key="booking.confirm_password_label" />
                                        <span className="text-red-500">
                                            {' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        placeholder={
                                            translations['booking.confirm_password_label']
                                        }
                                        type={confirm_password ? 'password' : 'text'}
                                        id="confirm_password"
                                        className={classNames(
                                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                            errors.confirm_password?.type ||
                                                errors.password?.type ===
                                                CognitoErrorTypes.PasswordMismatch
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('confirm_password', {
                                            required: true,
                                        })}
                                    />

                                    <i className={confirm_password ? "flex absolute justify-end -mt-8 mr-4 feather-eye right-0" : "feather-eye-off flex justify-end absolute right-0 -mt-8 mr-4"} onClick={() => setConfirmPassword(!confirm_password)} />{' '}
                                    <InputValidations
                                        field_error={errors.confirm_password}
                                        className="mb-1"
                                        required_key="booking.password_required"
                                    />
                                </fieldset>
                            </div>

                            <div className="flex md:items-center mb-6">
                                <input
                                    id="agree"
                                    type="checkbox"
                                    className="h-8 w-8 md:h-5 md:w-5 mr-3 border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                    onChange={(e) => {
                                        toggleAgree(e.target.checked)
                                    }}
                                    checked={agree}
                                />
                                <label htmlFor="agree">
                                    <Translation content_key="booking.terms_and_conditions_label_1" />
                                    <a href="/terms" target="_blank">
                                        &nbsp;
                                        <Translation content_key="booking.terms_and_conditions_label_2" />&nbsp;
                                    </a>
                                    <Translation content_key="booking.terms_and_conditions_label_3" />
                                    <a href="/privacy" target="_blank">
                                        &nbsp;
                                        <Translation content_key="booking.terms_and_conditions_label_4" />&nbsp;
                                    </a>
                                </label>
                            </div>

                            <div className="mb-5">
                                {Object.values(CognitoErrorTypes).includes(
                                    errors.email?.type as CognitoErrorTypes
                                ) ? (
                                    <div className="bg-red-100 rounded px-3 py-1 mb-5">
                                        {errors.email?.message && (
                                            <span className="text-sm text-red-700">
                                                {errors.email?.message}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    ''
                                )}
                                <button
                                    type="submit"
                                    className={classNames(
                                        'group relative w-full flex justify-center mx-auto',
                                        'py-2 mt-4 px-4 border border-transparent',
                                        'text-md rounded-xl text-white',
                                        agree || !loading
                                            ? 'focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                            : '',
                                        'h-14 flex items-center justify-center'
                                    )}
                                    style={{
                                        backgroundColor: !agree || loading
                                            ? `${primaryColor || default_primary_color}50`
                                            : primaryColor || default_primary_color,
                                    }}
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={!agree || loading}
                                >
                                    <Translation
                                        content_key="booking.sign_up"
                                        className="sm:text-xs md:text-base lg:text-base xl:text-base 2xl:text-base mr-3"
                                    />
                                    <span className="ml-3">
                                        {loading ? (
                                            <svg
                                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                        ) : (
                                            ''
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default ClientSignupModal
